import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiBase, authHeaders, resolveOrgId } from '../utils/authHeaders';
import { INVOICE_DECIDERS, mayDo, useExperience } from '../experience/experience';

const API_URL = apiBase();
const CONFIDENCE_HIGHLIGHT = 0.85;

/**
 * Phone/tablet layout switch. The tablet layout (two independently scrolling panes)
 * applies from Tailwind's `md` breakpoint (768px), matching the `md:` classes used
 * across the app; below it the review becomes three tabs.
 */
export const PHONE_QUERY = '(max-width: 767px)';
export const REVIEW_TABS = ['source', 'lines', 'vat'];
export const TAB_LABELS = { source: 'Source', lines: 'Lines', vat: 'VAT' };

/**
 * Tracks a media query. Returns false when matchMedia is unavailable (older jsdom,
 * SSR) so callers degrade to the wide layout rather than guessing.
 */
export function useIsPhone(query = PHONE_QUERY) {
  const read = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    const mql = window.matchMedia(query);
    return !!(mql && mql.matches);
  };
  const [matches, setMatches] = useState(read);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    if (!mql) return undefined;
    const onChange = (e) => setMatches(!!(e ? e.matches : read()));
    setMatches(!!mql.matches);
    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);
    return () => {
      if (typeof mql.removeEventListener === 'function') mql.removeEventListener('change', onChange);
      else if (typeof mql.removeListener === 'function') mql.removeListener('change', onChange);
    };
  }, [query]);

  return matches;
}

/** Stable command id for a click gesture — double-click reuses the same id. */
export function makeCommandId(prefix = 'inv-cmd') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Label for a line money/qty field per D1-3 (never claim "accuracy"). */
export function estimateLabel(line, field) {
  if (!line) return 'unavailable';
  const source = (line.source || line.extraction_source || '').toLowerCase();
  if (source.includes('json') || source.includes('xml') || source.includes('csv') || source === 'declared') {
    return 'declared by supplier file';
  }
  const val = line[field];
  if (val === undefined || val === null || val === '') return 'unavailable';
  return 'extraction estimate';
}

export function approveDisabled({ duplicateOf, overrideReason }) {
  if (!duplicateOf) return false;
  return !(overrideReason && String(overrideReason).trim().length > 0);
}

/** Prefer human invoice number / supplier over a raw UUID in the held list. */
export function heldInvoiceTitle(item) {
  if (!item) return 'Invoice';
  if (item.invoice_no) return item.invoice_no;
  if (item.supplier_name) return item.supplier_name;
  return 'Invoice (extraction pending)';
}

/**
 * Load ingest source bytes with the session Authorization header.
 * An iframe cannot send Bearer tokens, so we must fetch then blob: URL.
 */
export async function loadIngestSourceBlob(documentId, { orgId, fetchImpl = fetch } = {}) {
  if (!documentId) return { url: null, error: 'No source document' };
  const res = await fetchImpl(`${API_URL}/api/documents/ingest/${documentId}/view`, {
    headers: authHeaders({ orgId, json: true }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let detail = text;
    try {
      const parsed = JSON.parse(text);
      if (parsed && parsed.detail) detail = String(parsed.detail);
    } catch {
      /* keep text */
    }
    return { url: null, error: detail || `Source unavailable (${res.status})` };
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  return { url, error: null, contentType: blob.type || res.headers.get('content-type') || '' };
}

export default function InvoiceApprovalReview({ invoiceId = null }) {
  const [orgId] = useState(() => resolveOrgId());
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [receiptMatch, setReceiptMatch] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [resetInfo, setResetInfo] = useState(null);
  const [sourceBlobUrl, setSourceBlobUrl] = useState(null);
  const [sourceError, setSourceError] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const commandRef = useRef(null);
  const holdCommandRef = useRef(null);
  const sourceBlobRef = useRef(null);

  const revokeSourceBlob = useCallback(() => {
    if (sourceBlobRef.current) {
      URL.revokeObjectURL(sourceBlobRef.current);
      sourceBlobRef.current = null;
    }
    setSourceBlobUrl(null);
  }, []);
  // Selected tab survives a rotation because it is component state, not a layout effect.
  const [tab, setTab] = useState('source');
  const isPhone = useIsPhone();
  const tabRefs = useRef({});

  // The reset button only appears on demo tenants for owners/admins: anything else
  // (not allowed, 403, network error) simply hides it.
  const exp = useExperience();
  // pipeline_api.py DEMO_RESET_ROLES is owner/admin/manager, the same people who decide invoices; anyone else
  // would only get a 403, so they are not asked.
  const mayAskReset = exp.status === 'ready' && mayDo(exp, INVOICE_DECIDERS);
  const refreshResetInfo = useCallback(async () => {
    if (!mayAskReset) { setResetInfo(null); return; }
    try {
      const res = await fetch(`${API_URL}/api/review/demo-reset`, {
        headers: authHeaders({ orgId, json: true }),
      });
      const body = res.ok ? await res.json().catch(() => null) : null;
      setResetInfo(body && body.allowed === true ? body : null);
    } catch {
      setResetInfo(null);
    }
  }, [orgId, mayAskReset]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [a, n] = await Promise.all([
        fetch(`${API_URL}/api/documents/invoices?state=awaiting_approval`, {
          headers: authHeaders({ orgId, json: true }),
        }),
        fetch(`${API_URL}/api/documents/invoices?state=needs_review`, {
          headers: authHeaders({ orgId, json: true }),
        }),
      ]);
      const awaiting = a.ok ? (await a.json()).items || [] : [];
      const needs = n.ok ? (await n.json()).items || [] : [];
      const merged = [...awaiting, ...needs];
      setItems(merged);
      refreshResetInfo();
      // An explicit invoiceId (the mobile detail route) wins over the first row.
      setActiveId((prev) => invoiceId || prev || (merged[0] && merged[0].id) || null);
    } finally {
      setLoading(false);
    }
  }, [orgId, invoiceId, refreshResetInfo]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!activeId) {
      setDetail(null);
      setReceiptMatch(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const [dRes, mRes] = await Promise.all([
        fetch(`${API_URL}/api/review/invoices/${activeId}`, {
          headers: authHeaders({ orgId, json: true }),
        }),
        fetch(`${API_URL}/api/documents/invoices/${activeId}/receipt-match`, {
          headers: authHeaders({ orgId, json: true }),
        }),
      ]);
      if (cancelled) return;
      if (dRes.ok) setDetail(await dRes.json());
      else setDetail(null);
      if (mRes.ok) setReceiptMatch(await mRes.json());
      else setReceiptMatch(null);
      setOverrideReason('');
      setRejectReason('');
      setHoldReason('');
      commandRef.current = null;
      holdCommandRef.current = null;
    })();
    return () => {
      cancelled = true;
    };
  }, [activeId, orgId]);

  const invoice = detail?.invoice || detail;
  const onHold = !!(invoice && invoice.hold);
  const documentId = invoice?.document_id;
  // Approve, hold, reject and remove are manager decisions (pipeline_api.py); staff and viewers see who decides.
  const canDecide = mayDo(exp, INVOICE_DECIDERS);

  useEffect(() => {
    revokeSourceBlob();
    setSourceError(null);
    if (!documentId) {
      setSourceLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSourceLoading(true);
    (async () => {
      const result = await loadIngestSourceBlob(documentId, { orgId });
      if (cancelled) {
        if (result.url) URL.revokeObjectURL(result.url);
        return;
      }
      setSourceLoading(false);
      if (result.error) {
        setSourceError(result.error);
        return;
      }
      sourceBlobRef.current = result.url;
      setSourceBlobUrl(result.url);
    })();
    return () => {
      cancelled = true;
      revokeSourceBlob();
    };
  }, [documentId, orgId, revokeSourceBlob]);

  const ensureCommandId = () => {
    if (!commandRef.current) commandRef.current = makeCommandId();
    return commandRef.current;
  };

  const ensureHoldCommandId = () => {
    if (!holdCommandRef.current) holdCommandRef.current = makeCommandId('inv-hold');
    return holdCommandRef.current;
  };

  const postApprove = async () => {
    if (!activeId) return;
    const command_id = ensureCommandId();
    const body = { command_id };
    if (invoice?.duplicate_of) body.override_duplicate_reason = overrideReason.trim();
    const res = await fetch(`${API_URL}/api/review/invoices/${activeId}/approve`, {
      method: 'POST',
      headers: authHeaders({ orgId, json: true }),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      setToast(await res.text());
      return;
    }
    setToast('Approved');
    commandRef.current = null;
    setActiveId(null);
    await loadList();
  };

  const postReject = async () => {
    if (!activeId || !rejectReason.trim()) return;
    const command_id = ensureCommandId();
    const res = await fetch(`${API_URL}/api/review/invoices/${activeId}/reject`, {
      method: 'POST',
      headers: authHeaders({ orgId, json: true }),
      body: JSON.stringify({ command_id, reason: rejectReason.trim() }),
    });
    if (!res.ok) {
      setToast(await res.text());
      return;
    }
    setToast('Rejected');
    commandRef.current = null;
    setActiveId(null);
    await loadList();
  };

  const postHold = async () => {
    if (!activeId || !holdReason.trim()) return;
    const command_id = ensureHoldCommandId();
    const res = await fetch(`${API_URL}/api/review/invoices/${activeId}/hold`, {
      method: 'POST',
      headers: authHeaders({ orgId, json: true }),
      body: JSON.stringify({ command_id, reason: holdReason.trim() }),
    });
    if (!res.ok) {
      setToast(await res.text());
      return;
    }
    const body = await res.json().catch(() => ({}));
    setToast('On hold');
    holdCommandRef.current = null;
    setHoldReason('');
    setDetail((prev) => {
      if (!prev) return { invoice: body };
      if (prev.invoice) return { ...prev, invoice: { ...prev.invoice, ...body } };
      return { ...prev, ...body };
    });
    await loadList();
  };

  const postReleaseHold = async () => {
    if (!activeId) return;
    const command_id = ensureHoldCommandId();
    const res = await fetch(`${API_URL}/api/review/invoices/${activeId}/release-hold`, {
      method: 'POST',
      headers: authHeaders({ orgId, json: true }),
      body: JSON.stringify({ command_id }),
    });
    if (!res.ok) {
      setToast(await res.text());
      return;
    }
    const body = await res.json().catch(() => ({}));
    setToast('Hold released');
    holdCommandRef.current = null;
    setDetail((prev) => {
      const next = { ...(prev?.invoice ? prev.invoice : prev), ...body };
      delete next.hold;
      if (prev?.invoice) return { ...prev, invoice: next };
      return next;
    });
    await loadList();
  };

  const postResetDemo = async () => {
    const n = resetInfo?.invoices ?? 0;
    const ok = window.confirm(
      `Reset the demo? This removes ${n} uploaded invoice${n === 1 ? '' : 's'} and their review tasks ` +
        'so you can scan again. Seed data is kept. This cannot be undone.',
    );
    if (!ok) return;
    const res = await fetch(`${API_URL}/api/review/demo-reset`, {
      method: 'POST',
      headers: authHeaders({ orgId, json: true }),
    });
    if (!res.ok) {
      setToast(await res.text());
      return;
    }
    setToast('Demo reset — ready for a fresh scan');
    setDetail(null);
    setActiveId(null);
    await loadList();
  };

  const postDeleteUpload = async () => {
    if (!activeId) return;
    const ok = window.confirm(
      'Remove this upload so you can scan the same file again? This cannot be undone.',
    );
    if (!ok) return;
    const res = await fetch(`${API_URL}/api/review/invoices/${activeId}`, {
      method: 'DELETE',
      headers: authHeaders({ orgId, json: true }),
    });
    if (!res.ok) {
      setToast(await res.text());
      return;
    }
    setToast('Upload removed — you can re-scan the same file');
    setDetail(null);
    setActiveId(null);
    await loadList();
  };

  const dupBlocked = useMemo(
    () => approveDisabled({ duplicateOf: invoice?.duplicate_of, overrideReason }),
    [invoice, overrideReason],
  );

  // Panes are always in the DOM; on a phone only the selected one is shown, and from
  // `md` up every pane is visible regardless of the tab so rotation never hides data.
  const paneClass = (name) => (isPhone && tab !== name ? 'hidden md:block' : 'md:block');

  const onTabKeyDown = (event) => {
    const idx = REVIEW_TABS.indexOf(tab);
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (idx + 1) % REVIEW_TABS.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (idx - 1 + REVIEW_TABS.length) % REVIEW_TABS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = REVIEW_TABS.length - 1;
    if (next === null) return;
    event.preventDefault();
    const name = REVIEW_TABS[next];
    setTab(name);
    const el = tabRefs.current[name];
    if (el && typeof el.focus === 'function') el.focus();
  };

  return (
    <div className="mb-8" data-testid="invoice-approval-review">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="text-lg font-semibold text-slate-900">Held invoices</h2>
        {resetInfo && (
          <button
            type="button"
            data-testid="reset-demo"
            className="border border-slate-300 text-slate-700 px-3 py-1.5 min-h-[44px] rounded text-sm"
            onClick={postResetDemo}
            title="Remove uploaded invoices so the demo can be run again (demo tenants only)"
          >
            Reset demo
            <span data-testid="reset-demo-count" className="ml-1 text-xs text-slate-500">
              ({resetInfo.invoices} uploaded)
            </span>
          </button>
        )}
      </div>
      <p className="text-sm text-slate-600 mb-3">
        Source beside fields. Line values are extraction estimates until you approve.
      </p>
      {toast && (
        <div className="mb-3 bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded text-sm">
          {toast}
        </div>
      )}

      {/* Phones: tab strip. Hidden from md up, where both panes are visible at once. */}
      <div
        role="tablist"
        aria-label="Invoice review"
        data-testid="review-tabs"
        onKeyDown={onTabKeyDown}
        className="flex md:hidden border-b mb-3"
      >
        {REVIEW_TABS.map((name) => {
          const selected = tab === name;
          return (
            <button
              key={name}
              type="button"
              role="tab"
              id={`review-tab-${name}`}
              aria-selected={selected}
              aria-controls={`review-tabpanel-${name}`}
              data-testid={`review-tab-${name}`}
              tabIndex={selected ? 0 : -1}
              ref={(el) => { tabRefs.current[name] = el; }}
              onClick={() => setTab(name)}
              className={`flex-1 min-h-[44px] px-3 py-2 text-sm font-medium border-b-2 ${
                selected
                  ? 'border-emerald-700 text-emerald-900'
                  : 'border-transparent text-slate-600'
              }`}
            >
              {TAB_LABELS[name]}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-12 xl:col-span-3 bg-white border rounded-lg p-3">
          {loading && <p className="text-sm text-slate-400">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-slate-500">No invoices awaiting approval.</p>
          )}
          <ul className="space-y-1 max-h-64 overflow-auto md:max-h-56">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  data-testid={`held-invoice-${it.id}`}
                  className={`w-full text-left text-sm px-2 py-2.5 min-h-[44px] rounded ${
                    activeId === it.id ? 'bg-emerald-50 text-emerald-900' : 'hover:bg-slate-50'
                  }`}
                  onClick={() => setActiveId(it.id)}
                >
                  <div className="font-medium">{heldInvoiceTitle(it)}</div>
                  <div className="text-xs text-slate-500">
                    {it.supplier_name || 'Supplier pending'} · {it.state}
                    {it.hold ? ' · on hold' : ''}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Source pane — left of the pair from md up, scrolling on its own. */}
        <div
          id="review-tabpanel-source"
          role="tabpanel"
          aria-labelledby="review-tab-source"
          data-testid="review-pane-source"
          className={`md:col-span-6 xl:col-span-5 bg-white border rounded-lg p-3 min-h-[280px] md:max-h-[70vh] md:overflow-auto ${paneClass('source')}`}
        >
          <h3 className="font-semibold text-sm mb-1">Original document</h3>
          <p className="text-xs text-slate-500 mb-2">
            The uploaded PDF or photo — for checking extraction, not a form field.
          </p>
          {!documentId && <p className="text-sm text-slate-400">unavailable</p>}
          {documentId && sourceLoading && (
            <p className="text-sm text-slate-400" data-testid="invoice-source-loading">
              Loading document…
            </p>
          )}
          {documentId && sourceError && (
            <p
              className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2"
              data-testid="invoice-source-error"
            >
              {sourceError}
            </p>
          )}
          {sourceBlobUrl && !sourceError && (
            <iframe
              title="Invoice source"
              src={sourceBlobUrl}
              className="w-full h-[420px] border rounded bg-slate-50"
              data-testid="invoice-source-frame"
            />
          )}
        </div>

        {/* Right-hand column from md up: extracted lines above VAT, scrolling together. */}
        <div className="md:col-span-6 xl:col-span-4 md:max-h-[70vh] md:overflow-auto space-y-3">
          <div
            id="review-tabpanel-lines"
            role="tabpanel"
            aria-labelledby="review-tab-lines"
            data-testid="review-pane-lines"
            className={`bg-white border rounded-lg p-3 space-y-3 ${paneClass('lines')}`}
          >
            {!invoice && <p className="text-sm text-slate-400">Select an invoice</p>}
            {invoice && (
              <div>
                <h4 className="font-semibold text-sm mb-1">Lines</h4>
                {(invoice.lines || []).length === 0 && (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    No lines extracted yet — wait for OCR or remove and re-upload.
                  </p>
                )}
                <ul className="space-y-2">
                  {(invoice.lines || []).map((ln, idx) => {
                    const conf = Number(ln.confidence || 0);
                    const low = conf > 0 && conf < CONFIDENCE_HIGHLIGHT;
                    return (
                      <li
                        key={`${ln.item_id || ln.raw_desc}-${idx}`}
                        className={`border rounded p-2 text-sm ${low ? 'border-amber-400 bg-amber-50' : ''}`}
                      >
                        <div className="font-medium">{ln.raw_desc || ln.description}</div>
                        <div className="text-xs text-slate-600">
                          qty {ln.qty} · £{ln.unit_price} · line £{ln.line_total}
                        </div>
                        <div className="text-xs text-slate-500" data-testid={`estimate-label-${idx}`}>
                          {estimateLabel(ln, 'line_total')}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {receiptMatch && (
              <div data-testid="receipt-match-panel" className="border rounded p-2 text-sm">
                <h4 className="font-semibold text-sm mb-1">Receipt match</h4>
                <p className="text-xs text-slate-600 mb-1">
                  {receiptMatch.summary}
                  {receiptMatch.match_method ? ` · via ${receiptMatch.match_method}` : ''}
                </p>
                <ul className="space-y-1 max-h-32 overflow-auto">
                  {(receiptMatch.lines || []).map((ln) => (
                    <li key={`${ln.item_id}-${ln.status}`} className="text-xs">
                      {ln.description || ln.item_id}: inv {ln.invoiced_qty} / recv {ln.received_qty ?? '—'} ({ln.status})
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div
            id="review-tabpanel-vat"
            role="tabpanel"
            aria-labelledby="review-tab-vat"
            data-testid="review-pane-vat"
            className={`bg-white border rounded-lg p-3 space-y-3 ${paneClass('vat')}`}
          >
            {!invoice && <p className="text-sm text-slate-400">Select an invoice</p>}
            {invoice && (
              <>
                <div>
                  <div className="text-xs uppercase text-slate-500">Supplier</div>
                  <div className="font-medium">{invoice.supplier_name || 'unavailable'}</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Invoice #</div>
                    <div>{invoice.invoice_no || 'unavailable'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Date</div>
                    <div>{invoice.date || 'unavailable'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Total</div>
                    <div>£{invoice.totals?.invoice_total ?? 'unavailable'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">VAT</div>
                    <div>£{invoice.totals?.vat_total ?? 'unavailable'}</div>
                  </div>
                </div>

                {invoice.duplicate_of && (
                  <div
                    className="bg-rose-50 border border-rose-200 text-rose-900 rounded p-2 text-sm"
                    data-testid="duplicate-banner"
                  >
                    Possible duplicate of invoice id {invoice.duplicate_of}. Enter a reason to approve.
                    <textarea
                      className="mt-2 w-full border rounded p-2 text-sm"
                      data-testid="duplicate-reason"
                      rows={2}
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="Why this is not a duplicate"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Approve/reject. Sticky to the bottom of the viewport on a phone so the
            decision is always one tap away; static in the layout from md up. */}
        <div
          data-testid="review-action-bar"
          className="sticky bottom-0 z-10 -mx-4 px-4 py-3 bg-white border-t md:static md:mx-0 md:px-0 md:py-0 md:border-0 md:col-span-12 xl:col-span-4 xl:col-start-9"
        >
          {invoice && !canDecide && (
            <p className="text-sm text-slate-700 md:pt-2 md:border-t" data-testid="review-manager-only">
              {onHold ? `On hold${invoice.hold?.reason ? `: ${invoice.hold.reason}` : ''}. ` : ''}
              A manager checks this invoice and approves, holds or rejects it. Nothing changes in stock or prices until then.
            </p>
          )}
          {invoice && canDecide && (
            <div className="flex flex-col gap-2 md:pt-2 md:border-t">
              {onHold && (
                <div
                  className="bg-amber-50 border border-amber-200 text-amber-950 rounded p-2 text-sm"
                  data-testid="hold-banner"
                >
                  On hold{invoice.hold?.reason ? `: ${invoice.hold.reason}` : ''}
                  <button
                    type="button"
                    data-testid="release-hold-invoice"
                    className="mt-2 w-full border border-amber-400 text-amber-950 px-3 py-2 min-h-[44px] rounded text-sm"
                    onClick={postReleaseHold}
                  >
                    Release hold
                  </button>
                </div>
              )}
              {!onHold && (
                <>
                  <textarea
                    className="w-full border rounded p-2 text-sm"
                    data-testid="hold-reason"
                    rows={2}
                    value={holdReason}
                    onChange={(e) => setHoldReason(e.target.value)}
                    placeholder="Hold reason (required)"
                  />
                  <button
                    type="button"
                    data-testid="hold-invoice"
                    className="border border-amber-400 text-amber-950 px-3 py-2 min-h-[44px] rounded text-sm disabled:opacity-40"
                    disabled={!holdReason.trim()}
                    onClick={postHold}
                  >
                    Hold
                  </button>
                </>
              )}
              {/* invoice_approval.py approves only "awaiting_approval": an invoice whose lines could not be read
                  ("needs_review") is corrected first, never approved as it stands (rehearsal 28 Oct). */}
              {invoice.state && invoice.state !== 'awaiting_approval' ? (
                <p className="text-sm text-slate-700" data-testid="approve-needs-correction">
                  What was read from this invoice needs checking, so it cannot be approved yet.{' '}
                  <a className="underline" href={`/invoices/${encodeURIComponent(invoice.id || activeId)}`}>Correct what was read</a> first.
                </p>
              ) : (
                <button
                  type="button"
                  data-testid="approve-invoice"
                  className="bg-emerald-700 text-white px-3 py-2 min-h-[44px] rounded text-sm disabled:opacity-40"
                  disabled={dupBlocked}
                  onClick={postApprove}
                >
                  Approve
                </button>
              )}
              <textarea
                className="w-full border rounded p-2 text-sm"
                data-testid="reject-reason"
                rows={2}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reject reason (required)"
              />
              <button
                type="button"
                data-testid="reject-invoice"
                className="border border-rose-300 text-rose-800 px-3 py-2 min-h-[44px] rounded text-sm disabled:opacity-40"
                disabled={!rejectReason.trim()}
                onClick={postReject}
              >
                Reject
              </button>
              <button
                type="button"
                data-testid="delete-upload-invoice"
                className="border border-slate-300 text-slate-700 px-3 py-2 min-h-[44px] rounded text-sm"
                onClick={postDeleteUpload}
              >
                Remove upload
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
